import { useEffect, useState } from "react";
import { useParams, useLocation } from "react-router-dom";
import { Badge, Box, HStack, Link, VStack, Text, Spinner, IconButton } from "@chakra-ui/react";
import { HiOutlineCpuChip, HiStar, HiPhone, HiEnvelope, HiFlag, HiClock, HiArrowPath } from "react-icons/hi2";
import useSetPageHeader from "@/components/hooks/useSetPageHeader";
import { LeadService } from "@/services/LeadService";
import { toaster } from "@/components/ui/toaster";
import dayjs from "dayjs";
import LeadStatusBadge from "./components/LeadStatusBadge";
import LeadDataTab from "./components/lead/LeadDataTab";
import LeadTimelineTab from "./components/lead/LeadTimelineTab";
import LeadNotesTab from "./components/lead/LeadNotesTab";
import LeadInteractionsTab from "./components/lead/LeadInteractionsTab";
import LeadAttachmentsTab from "./components/lead/LeadAttachmentsTab";
import useLeadSingleActions from "./hooks/useLeadSingleActions";

const LeadDetailPage = () => {
    const { leadId } = useParams();
    const location = useLocation();

    const [lead, setLead] = useState(null);
    const [notes, setNotes] = useState([]);
    const [timeline, setTimeline] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isNotesLoading, setIsNotesLoading] = useState(true);
    const [notesSearch, setNotesSearch] = useState('');
    const [notesSortDirection, setNotesSortDirection] = useState('desc');
    const [debouncedNotesSearch, setDebouncedNotesSearch] = useState('');
    const [notesFetchKey, setNotesFetchKey] = useState(0);
    const [timelineFetchKey, setTimelineFetchKey] = useState(0);
    const [isTimelineLoading, setIsTimelineLoading] = useState(true);
    const [attachments, setAttachments] = useState([]);
    const [isAttachmentsLoading, setIsAttachmentsLoading] = useState(true);
    const [isFavLoading, setIsFavLoading] = useState(false);
    const [activeTab, setActiveTab] = useState(location.state?.tab ?? "data");
    const [highlightId, setHighlightId] = useState(location.state?.highlightId ?? null);

    const { openStatusModal, openNoteModal, openTimelineModal, modals: singleActionModals } = useLeadSingleActions({
        onStatusChanged: (_, updated) => setLead(prev => ({ ...prev, status: updated.status })),
        onNoteAdded: () => setNotesFetchKey(k => k + 1),
        onTimelineEntryAdded: () => setTimelineFetchKey(k => k + 1),
    });

    useSetPageHeader("Szczegóły leada", "", <HiOutlineCpuChip />);

    useEffect(() => {
        setIsLoading(true);
        LeadService.getById(leadId).then(setLead).finally(() => setIsLoading(false));
    }, [leadId]);

    useEffect(() => {
        const timer = setTimeout(() => setDebouncedNotesSearch(notesSearch), 400);
        return () => clearTimeout(timer);
    }, [notesSearch]);

    useEffect(() => {
        setIsNotesLoading(true);
        LeadService.getNotes(leadId, 'createdAt', notesSortDirection, debouncedNotesSearch || null)
            .then(d => setNotes(d ?? []))
            .finally(() => setIsNotesLoading(false));
    }, [leadId, notesSortDirection, debouncedNotesSearch, notesFetchKey]);

    useEffect(() => {
        setIsTimelineLoading(true);
        LeadService.getTimeline(leadId).then(d => setTimeline(d ?? [])).finally(() => setIsTimelineLoading(false));
    }, [leadId, timelineFetchKey]);

    useEffect(() => {
        setIsAttachmentsLoading(true);
        LeadService.getAttachments(leadId).then(d => setAttachments(d ?? [])).finally(() => setIsAttachmentsLoading(false));
    }, [leadId]);

    const handleToggleFavorite = async () => {
        setIsFavLoading(true);
        const updated = await LeadService.toggleFavorite(leadId);
        setLead(prev => ({ ...prev, isFavourite: updated.isFavourite }));
        setIsFavLoading(false);
    };

    const handleHeaderAddNote = () => openNoteModal(leadId);

    const handleOpenAttachment = (att) => {
        if (!att.path) return;
        const url = `${process.env.REACT_APP_BASE_URL || '/mlapi'}/Lead/${leadId}/attachments/file?name=${encodeURIComponent(att.path)}`;
        window.open(url, '_blank', 'noopener,noreferrer');
    };

    const handleDownloadAttachment = async (att) => {
        if (!att.path) return;
        try {
            const response = await LeadService.downloadAttachment(leadId, att.path);
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const a = document.createElement('a');
            a.href = url;
            a.download = att.name ?? att.path.split('/').pop();
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch {
            toaster.create({ type: "error", title: "Nie udało się pobrać pliku." });
        }
    };

    const handleUploadAttachments = async (files) => {
        const newAtts = await LeadService.addAttachments(leadId, files);
        setAttachments(prev => [...prev, ...(newAtts ?? [])]);
    };

    const handleDownloadAllZip = async () => {
        try {
            const response = await LeadService.downloadAllAttachmentsZip(leadId);
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const a = document.createElement('a');
            a.href = url;
            a.download = `attachments_${leadId}.zip`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch {
            toaster.create({ type: "error", title: "Nie udało się pobrać załączników." });
        }
    };

    if (isLoading) {
        return <VStack w="full" py={12} align="center"><Spinner color="brand.400" size="lg" /></VStack>;
    }

    if (!lead) {
        return <VStack w="full" py={12} align="center"><Text color="text.grey">Nie znaleziono leada.</Text></VStack>;
    }

    const fullName = [lead.firstname, lead.lastname].filter(Boolean).join(" ") || "-";

    const TABS = [
        { key: "data", label: "Dane" },
        { key: "timeline", label: "Oś czasu", count: timeline.length },
        { key: "notes", label: "Notatki", count: notes.length },
        { key: "interactions", label: "Rozmowy" },
        { key: "attachments", label: "Załączniki", count: attachments.length },
    ];

    return (
        <>
            <VStack w="full" py={6} gap={4} align="flex-start">
                <Box w="full" bg="bg.card" border="1px solid" borderColor="border.primary" borderRadius={12} p={5}>
                    <HStack justify="space-between" align="flex-start" flexWrap="wrap" gap={4}>
                        <VStack align="flex-start" gap={2} flex={1} minW="200px">
                            <HStack gap={2}>
                                <Text fontWeight={700} fontSize="2xl" color="white">{fullName}</Text>
                                <IconButton variant="ghost" size="sm" aria-label="Ulubione" color={lead.isFavourite ? "yellow.400" : "gray.500"} loading={isFavLoading} onClick={handleToggleFavorite}>
                                    <HiStar />
                                </IconButton>
                            </HStack>
                            <Box as="button" cursor="pointer" onClick={() => openStatusModal(leadId, lead.status)} outline="none">
                                <LeadStatusBadge status={lead.status} />
                            </Box>
                            {(lead.company || lead.jobTitle) && (
                                <Text color="text.grey" fontSize="sm">{[lead.jobTitle, lead.company].filter(Boolean).join(" @ ")}</Text>
                            )}
                            {lead.phoneNumber && (
                                <HStack gap={2}>
                                    <Box color="text.grey" fontSize="sm" flexShrink={0}><HiPhone /></Box>
                                    <Link href={`tel:${lead.phoneNumber}`} color="white" fontSize="sm">{lead.phoneNumber}</Link>
                                </HStack>
                            )}
                            {lead.email && (
                                <HStack gap={2}>
                                    <Box color="text.grey" fontSize="sm" flexShrink={0}><HiEnvelope /></Box>
                                    <Link href={`mailto:${lead.email}`} color="white" fontSize="sm">{lead.email}</Link>
                                </HStack>
                            )}
                            <HStack gap={2}>
                                <Box color="text.grey" fontSize="sm" flexShrink={0}><HiFlag /></Box>
                                <Text color="white" fontSize="sm">{lead.language ?? "Nieznany"}</Text>
                            </HStack>
                        </VStack>
                        <VStack align="flex-end" gap={2} flexShrink={0}>
                            <HStack gap={2}>
                                <Box color="text.grey" fontSize="sm" flexShrink={0}><HiClock /></Box>
                                <Text color="text.grey" fontSize="sm">Utworzono: <Text as="span" color="white">{dayjs(lead.createdAt).format("DD.MM.YYYY HH:mm")}</Text></Text>
                            </HStack>
                            {lead.updatedAt && (
                                <HStack gap={2}>
                                    <Box color="text.grey" fontSize="sm" flexShrink={0}><HiArrowPath /></Box>
                                    <Text color="text.grey" fontSize="sm">Ostatnia aktywność: <Text as="span" color="white">{dayjs(lead.updatedAt).format("DD.MM.YYYY HH:mm")}</Text></Text>
                                </HStack>
                            )}
                        </VStack>
                    </HStack>
                </Box>

                <Box w="full">
                    <HStack gap={0} borderBottom="1px solid" borderColor="border.primary" w="full">
                        {TABS.map(tab => (
                            <Box
                                key={tab.key} as="button" display="flex" alignItems="center" gap="6px" px={4} py={3} cursor="pointer"
                                color={activeTab === tab.key ? "white" : "text.grey"}
                                borderBottomWidth="2px" borderBottomStyle="solid" borderBottomColor={activeTab === tab.key ? "brand.400" : "transparent"}
                                fontWeight={activeTab === tab.key ? 600 : 400} fontSize="sm" onClick={() => setActiveTab(tab.key)}
                                transition="all 0.15s" mb="-1px" bg="transparent" outline="none" _hover={{ color: "white" }} userSelect="none"
                            >
                                <Text as="span" fontSize="sm" fontWeight="inherit">{tab.label}</Text>
                                {tab.count > 0 && (
                                    <Badge size="xs" variant="subtle" colorPalette={activeTab === tab.key ? "blue" : "gray"} borderRadius={10} fontSize="2xs" minW="18px" textAlign="center">
                                        {tab.count}
                                    </Badge>
                                )}
                            </Box>
                        ))}
                    </HStack>

                    <Box pt={4}>
                        {activeTab === "data" && <LeadDataTab lead={lead} />}
                        {activeTab === "timeline" && (
                            <LeadTimelineTab timeline={timeline} isLoading={isTimelineLoading} onAddEntry={() => openTimelineModal(leadId)} highlightId={highlightId} onHighlightClear={() => setHighlightId(null)} />
                        )}
                        {activeTab === "notes" && (
                            <LeadNotesTab
                                notes={notes} isLoading={isNotesLoading} onAddNote={handleHeaderAddNote}
                                search={notesSearch} onSearchChange={setNotesSearch}
                                sortDirection={notesSortDirection} onToggleSortDirection={() => setNotesSortDirection(d => d === 'desc' ? 'asc' : 'desc')}
                                highlightId={highlightId} onHighlightClear={() => setHighlightId(null)}
                            />
                        )}
                        {activeTab === "interactions" && <LeadInteractionsTab leadId={leadId} />}
                        {activeTab === "attachments" && (
                            <LeadAttachmentsTab
                                attachments={attachments}
                                isLoading={isAttachmentsLoading}
                                onOpenAttachment={handleOpenAttachment}
                                onDownloadAttachment={handleDownloadAttachment}
                                onUploadAttachments={handleUploadAttachments}
                                onDownloadAllZip={handleDownloadAllZip}
                            />
                        )}
                    </Box>
                </Box>
            </VStack>
            {singleActionModals}
        </>
    );
};

export default LeadDetailPage;
